import { useEffect, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { TlaaqLogo } from '../../components/brand/TlaaqLogo'
import { LocaleSwitcher } from '../../components/locale/LocaleSwitcher'
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport'

export type PublicDestination = 'landing' | 'privacy' | 'terms' | 'faq' | 'support' | 'login'
export type PublicPage = Extract<PublicDestination, 'privacy' | 'terms' | 'faq' | 'support'>

interface PublicPageLayoutProps {
  active: PublicPage
  children: ReactNode
  onNavigate: (destination: PublicDestination) => void
}

const PUBLIC_LINKS: ReadonlyArray<{ destination: PublicPage; href: string }> = [
  { destination: 'privacy', href: '/privacy' },
  { destination: 'terms', href: '/terms' },
  { destination: 'faq', href: '/faq' },
  { destination: 'support', href: '/support' },
]

function PublicLink({ destination, href, active, onNavigate }: {
  destination: PublicPage
  href: string
  active: boolean
  onNavigate: (destination: PublicDestination) => void
}) {
  const { t } = useTranslation()
  return (
    <a
      href={href}
      aria-current={active ? 'page' : undefined}
      onClick={(event) => {
        event.preventDefault()
        onNavigate(destination)
      }}
      style={{
        color: active ? 'var(--brand-500)' : 'var(--brand-900)',
        fontSize: 'var(--fs-14)',
        fontWeight: active ? 'var(--fw-bold)' : 'var(--fw-medium)',
        textDecoration: 'none',
      }}
    >
      {t(`public.site.nav.${destination}`)}
    </a>
  )
}

export function PublicPageLayout({ active, children, onNavigate }: PublicPageLayoutProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()

  useEffect(() => {
    document.body.setAttribute('data-public-page', '')
    return () => document.body.removeAttribute('data-public-page')
  }, [])

  return (
    <div style={{ background: 'var(--white)', color: 'var(--fg)', minHeight: '100dvh', overflowX: 'clip' }}>
      <a className="landing-skip-link" href="#public-main">{t('public.site.skip')}</a>
      <header style={{ background: 'var(--white)', borderBottom: '1px solid var(--border-subtle)' }}>
        <div style={{ alignItems: 'center', display: 'flex', gap: isMobile ? 'var(--sp-4)' : 'var(--sp-10)', justifyContent: 'space-between', minHeight: isMobile ? 64 : 76, padding: isMobile ? 'var(--sp-4) var(--sp-6)' : 'var(--sp-4) var(--sp-8)' }}>
          <a
            href="/"
            aria-label={t('public.site.nav.home')}
            onClick={(event) => { event.preventDefault(); onNavigate('landing') }}
            style={{ alignItems: 'center', display: 'inline-flex', flexShrink: 0 }}
          >
            <TlaaqLogo height={isMobile ? 26 : 36} />
          </a>
          {!isMobile ? (
            <nav aria-label={t('public.site.nav.aria')} style={{ display: 'flex', flex: 1, gap: 'var(--sp-8)' }}>
              {PUBLIC_LINKS.map(link => <PublicLink key={link.destination} {...link} active={active === link.destination} onNavigate={onNavigate} />)}
            </nav>
          ) : null}
          <div style={{ alignItems: 'center', display: 'flex', gap: isMobile ? 'var(--sp-3)' : 'var(--sp-5)' }}>
            <LocaleSwitcher />
            {!isMobile ? (
              <a href="/login" onClick={(event) => { event.preventDefault(); onNavigate('login') }} style={{ color: 'var(--brand-900)', fontSize: 'var(--fs-14)', textDecoration: 'none' }}>
                {t('public.site.nav.signIn')}
              </a>
            ) : null}
          </div>
        </div>
        {isMobile ? (
          <nav aria-label={t('public.site.nav.aria')} style={{ display: 'flex', gap: 'var(--sp-5)', justifyContent: 'space-between', overflowX: 'auto', padding: '0 var(--sp-6) var(--sp-4)' }}>
            {PUBLIC_LINKS.map(link => <PublicLink key={link.destination} {...link} active={active === link.destination} onNavigate={onNavigate} />)}
          </nav>
        ) : null}
      </header>

      <main id="public-main">{children}</main>

      <footer style={{ alignItems: isMobile ? 'flex-start' : 'center', background: 'var(--brand-900)', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 'var(--sp-6)' : 'var(--sp-12)', justifyContent: 'space-between', padding: isMobile ? 'var(--sp-8) var(--sp-6)' : 'var(--sp-12) var(--sp-24)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', maxWidth: 360 }}>
          <TlaaqLogo height={isMobile ? 28 : 34} variant="onDark" />
          <span style={{ color: 'var(--brand-100)', fontSize: 'var(--fs-14)', lineHeight: 'var(--lh-normal)' }}>{t('public.site.footer.description')}</span>
        </div>
        <nav aria-label={t('public.site.footer.aria')} style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-6)' }}>
          {PUBLIC_LINKS.map(link => (
            <a key={link.destination} href={link.href} onClick={(event) => { event.preventDefault(); onNavigate(link.destination) }} style={{ color: 'var(--white)', fontSize: 'var(--fs-14)', fontWeight: 'var(--fw-bold)', textDecoration: 'none' }}>
              {t(`public.site.nav.${link.destination}`)}
            </a>
          ))}
        </nav>
        <div style={{ color: 'var(--brand-100)', display: 'flex', flexDirection: 'column', fontSize: 'var(--fs-13)', gap: 'var(--sp-2)', textAlign: 'start' }}>
          <span>{t('public.site.footer.identity')}</span>
          <span>{t('public.site.footer.location')}</span>
        </div>
      </footer>
    </div>
  )
}
